/**
 * React Query hooks for every API resource. This file is the client side of the API contract
 * (see src/shared/schemas.ts and the endpoint list in src/server/routes). Pages use only these hooks.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { CutoffSuggestion } from '@shared/cutoff';
import { DEFAULT_SETTINGS, sameSettingValue } from '@shared/schemas';
import type { UsageStatus } from '@shared/usage';
import { setUsageSharing, track } from '@/lib/usage';
import { photoFileName, type PhotoSources, type PreparedPhoto } from '@/lib/photo';
import type {
  Backup,
  BeanCreate,
  BeanUpdate,
  BeanWithStats,
  BrewCreate,
  BrewDetail,
  BrewListQuery,
  BrewListResponse,
  BrewMethod,
  BrewMethodCreate,
  BrewMethodUpdate,
  BrewUpdate,
  Chat,
  ChatAppend,
  ChatExchangeCreate,
  ChatSummary,
  GrinderCreate,
  GrinderUpdate,
  GrinderWithStats,
  LastBrewSuggestion,
  MilkSuggestion,
  ServerInfo,
  Settings,
  SettingsUpdate,
  Stats,
  Vessel,
  VesselCreate,
  VesselUpdate,
} from '@shared/schemas';
import { api, ApiRequestError, qs } from './client';

export type ArchivedFilter = 'active' | 'archived' | 'all';

export const keys = {
  beans: (archived: ArchivedFilter = 'active') => ['beans', archived] as const,
  bean: (id: number) => ['bean', id] as const,
  grinders: (archived: ArchivedFilter = 'active') => ['grinders', archived] as const,
  grinder: (id: number) => ['grinder', id] as const,
  vessels: (archived: ArchivedFilter = 'active') => ['vessels', archived] as const,
  methods: (archived: ArchivedFilter = 'active') => ['methods', archived] as const,
  method: (id: number) => ['method', id] as const,
  brews: (q: BrewListQuery = {}) => ['brews', q] as const,
  brew: (id: number) => ['brew', id] as const,
  lastBrew: (p: { beanId?: number | null; grinderId?: number | null; methodId?: number | null }) => ['lastBrew', p] as const,
  milkSuggestion: (methodId: number | null) => ['milkSuggestion', methodId] as const,
  cutoffSuggestion: (methodId: number | null) => ['cutoffSuggestion', methodId] as const,
  stats: ['stats'] as const,
  settings: ['settings'] as const,
  info: ['info'] as const,
  usage: ['usage'] as const,
  chats: ['chats'] as const,
  chat: (id: number) => ['chat', id] as const,
};

/** Brews affect bean/grinder stats, suggestions and dashboard stats — invalidate broadly. */
function invalidateBrewRelated(qc: QueryClient) {
  for (const k of ['brews', 'brew', 'beans', 'bean', 'grinders', 'grinder', 'lastBrew', 'milkSuggestion', 'cutoffSuggestion', 'stats']) {
    void qc.invalidateQueries({ queryKey: [k] });
  }
}

// ---------------------------------------------------------------- beans
export function useBeans(archived: ArchivedFilter = 'active') {
  return useQuery({ queryKey: keys.beans(archived), queryFn: () => api.get<BeanWithStats[]>(`/beans${qs({ archived })}`) });
}
export function useBean(id: number | null | undefined) {
  return useQuery({ queryKey: keys.bean(id ?? 0), queryFn: () => api.get<BeanWithStats>(`/beans/${id}`), enabled: id != null && id > 0 });
}
export function useCreateBean() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BeanCreate) => api.post<BeanWithStats>('/beans', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beans'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}
export function useUpdateBean() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: BeanUpdate & { id: number }) => api.patch<BeanWithStats>(`/beans/${id}`, patch),
    onSuccess: (bean) => {
      qc.setQueryData(keys.bean(bean.id), bean);
      void qc.invalidateQueries({ queryKey: ['beans'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
      void qc.invalidateQueries({ queryKey: ['brews'] });
    },
  });
}
/** Bag photo URL, or null without one. Versioned by photoUpdatedAt, so the browser can cache it for good. */
export function beanPhotoUrl(bean: Pick<BeanWithStats, 'id' | 'photoUpdatedAt'>, size: 'full' | 'thumb' = 'full'): string | null {
  return bean.photoUpdatedAt ? `/api/beans/${bean.id}/photo${qs({ v: bean.photoUpdatedAt, size: size === 'thumb' ? size : undefined })}` : null;
}
export function beanPhotoSources(bean: Pick<BeanWithStats, 'id' | 'photoUpdatedAt'>): PhotoSources | null {
  const full = beanPhotoUrl(bean);
  const thumb = beanPhotoUrl(bean, 'thumb');
  return full && thumb ? { full, thumb } : null;
}
function onBeanPhotoChanged(qc: QueryClient, bean: BeanWithStats) {
  qc.setQueryData(keys.bean(bean.id), bean);
  void qc.invalidateQueries({ queryKey: ['beans'] });
}
/** `photo` comes from preparePhoto(): the downsized photo. The server makes its thumbnail. */
export function useSetBeanPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, photo }: { id: number; photo: PreparedPhoto }) => {
      const form = new FormData();
      form.append('photo', photo.full, photoFileName('photo', photo.full));
      return api.put<BeanWithStats>(`/beans/${id}/photo`, form);
    },
    onSuccess: (bean) => onBeanPhotoChanged(qc, bean),
  });
}
/** Gives a bean a copy of another bean's photo, on the server ("New bag of these"). */
export function useCopyBeanPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fromBeanId }: { id: number; fromBeanId: number }) => api.put<BeanWithStats>(`/beans/${id}/photo`, { fromBeanId }),
    onSuccess: (bean) => onBeanPhotoChanged(qc, bean),
  });
}
export function useDeleteBeanPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<BeanWithStats>(`/beans/${id}/photo`),
    onSuccess: (bean) => onBeanPhotoChanged(qc, bean),
  });
}
/** What Undo needs to put a deleted bean back: the bean, the brews it had (Delete leaves them without a bean) and its photo. */
export interface DeletedBean {
  bean: BeanWithStats;
  brewIds: number[];
  photo: Blob | null;
}
async function beanBrewIds(id: number, total: number): Promise<number[]> {
  const ids: number[] = [];
  for (let offset = 0; offset === 0 || offset < total; offset += 500) {
    const page = await api.get<BrewListResponse>(`/brews${qs({ beanId: id, limit: 500, offset })}`);
    ids.push(...page.items.map((b) => b.id));
    total = page.total;
  }
  return ids;
}
/** Deletes the bean after reading what Undo needs (the photo's URL is cached for good, so it is rarely a download). */
export function useDeleteBean() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bean: BeanWithStats): Promise<DeletedBean> => {
      const url = beanPhotoUrl(bean);
      const [brewIds, photo] = await Promise.all([
        beanBrewIds(bean.id, bean.brewCount),
        url
          ? fetch(url).then((res) => {
              if (!res.ok) throw new ApiRequestError(`Couldn’t read the photo (${res.status})`, res.status);
              return res.blob();
            })
          : null,
      ]);
      await api.delete(`/beans/${bean.id}`);
      return { bean, brewIds, photo };
    },
    onSuccess: () => invalidateBrewRelated(qc),
  });
}
/**
 * Undo for Delete: puts the bean back with its id and brews, then its photo. A plain function rather than a mutation,
 * because its toast outlives the page that deleted the bean. Once the bean is back, a photo that fails comes back as
 * `photoError` rather than a rejection.
 */
export function useRestoreBean() {
  const qc = useQueryClient();
  return async ({ bean, brewIds, photo }: DeletedBean): Promise<{ bean: BeanWithStats; photoError: Error | null }> => {
    let b = await api.post<BeanWithStats>(`/beans/${bean.id}/restore`, { ...bean, brewIds });
    let photoError: Error | null = null;
    if (photo) {
      const form = new FormData();
      form.append('photo', photo, photoFileName('photo', photo));
      try {
        b = await api.put<BeanWithStats>(`/beans/${bean.id}/photo`, form);
      } catch (e) {
        photoError = e as Error;
      }
    }
    qc.setQueryData(keys.bean(b.id), b);
    invalidateBrewRelated(qc);
    return { bean: b, photoError };
  };
}

// ---------------------------------------------------------------- grinders
export function useGrinders(archived: ArchivedFilter = 'active') {
  return useQuery({ queryKey: keys.grinders(archived), queryFn: () => api.get<GrinderWithStats[]>(`/grinders${qs({ archived })}`) });
}
export function useGrinder(id: number | null | undefined) {
  return useQuery({ queryKey: keys.grinder(id ?? 0), queryFn: () => api.get<GrinderWithStats>(`/grinders/${id}`), enabled: id != null && id > 0 });
}
export function useCreateGrinder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GrinderCreate) => api.post<GrinderWithStats>('/grinders', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['grinders'] }),
  });
}
export function useUpdateGrinder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: GrinderUpdate & { id: number }) => api.patch<GrinderWithStats>(`/grinders/${id}`, patch),
    onSuccess: (g) => {
      qc.setQueryData(keys.grinder(g.id), g);
      void qc.invalidateQueries({ queryKey: ['grinders'] });
      void qc.invalidateQueries({ queryKey: ['brews'] });
    },
  });
}
export function useDeleteGrinder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/grinders/${id}`),
    onSuccess: () => {
      invalidateBrewRelated(qc);
      void qc.invalidateQueries({ queryKey: ['vessels'] });
    },
  });
}

// ---------------------------------------------------------------- vessels
export function useVessels(archived: ArchivedFilter = 'active') {
  return useQuery({ queryKey: keys.vessels(archived), queryFn: () => api.get<Vessel[]>(`/vessels${qs({ archived })}`) });
}
export function useCreateVessel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VesselCreate) => api.post<Vessel>('/vessels', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vessels'] }),
  });
}
export function useUpdateVessel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: VesselUpdate & { id: number }) => api.patch<Vessel>(`/vessels/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vessels'] }),
  });
}
export function useDeleteVessel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/vessels/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vessels'] });
      void qc.invalidateQueries({ queryKey: ['methods'] });
    },
  });
}

// ---------------------------------------------------------------- methods
export function useMethods(archived: ArchivedFilter = 'active') {
  return useQuery({ queryKey: keys.methods(archived), queryFn: () => api.get<BrewMethod[]>(`/methods${qs({ archived })}`) });
}
export function useMethod(id: number | null | undefined) {
  return useQuery({ queryKey: keys.method(id ?? 0), queryFn: () => api.get<BrewMethod>(`/methods/${id}`), enabled: id != null && id > 0 });
}
export function useCreateMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BrewMethodCreate) => api.post<BrewMethod>('/methods', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['methods'] }),
  });
}
export function useUpdateMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: BrewMethodUpdate & { id: number }) => api.patch<BrewMethod>(`/methods/${id}`, patch),
    onSuccess: (m) => {
      qc.setQueryData(keys.method(m.id), m);
      void qc.invalidateQueries({ queryKey: ['methods'] });
    },
  });
}
export function useDeleteMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/methods/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['methods'] });
      invalidateBrewRelated(qc);
    },
  });
}

// ---------------------------------------------------------------- brews
export function useBrews(query: BrewListQuery = {}, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.brews(query),
    queryFn: () => api.get<BrewListResponse>(`/brews${qs(query as Record<string, number | undefined>)}`),
    enabled,
  });
}
export function useBrew(id: number | null | undefined) {
  return useQuery({ queryKey: keys.brew(id ?? 0), queryFn: () => api.get<BrewDetail>(`/brews/${id}`), enabled: id != null && id > 0 });
}
export function useCreateBrew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BrewCreate) => api.post<BrewDetail>('/brews', input),
    onSuccess: (b) => {
      qc.setQueryData(keys.brew(b.id), b);
      invalidateBrewRelated(qc);
    },
  });
}
export function useUpdateBrew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: BrewUpdate & { id: number }) => api.patch<BrewDetail>(`/brews/${id}`, patch),
    onSuccess: (b) => {
      qc.setQueryData(keys.brew(b.id), b);
      invalidateBrewRelated(qc);
    },
  });
}
export function useDeleteBrew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/brews/${id}`),
    onSuccess: () => invalidateBrewRelated(qc),
  });
}
/**
 * Undo for Delete: puts the brew back as the app had it (GET /brews/:id), with its id. A plain function rather than a
 * mutation, because its toast outlives the page that deleted the brew.
 */
export function useRestoreBrew() {
  const qc = useQueryClient();
  return async (brew: BrewDetail) => {
    const b = await api.post<BrewDetail>(`/brews/${brew.id}/restore`, brew);
    qc.setQueryData(keys.brew(b.id), b);
    invalidateBrewRelated(qc);
    return b;
  };
}

/** Previous brew best matching the selection — used to prefill grind setting/dose in the brew wizard. */
export function useLastBrewSuggestion(p: { beanId?: number | null; grinderId?: number | null; methodId?: number | null }) {
  return useQuery({
    queryKey: keys.lastBrew(p),
    queryFn: () => api.get<LastBrewSuggestion>(`/suggestions/last-brew${qs(p)}`),
    enabled: p.beanId != null || p.grinderId != null,
  });
}

/** The method's latest drink and the milks used before — prefills the drink card (and the milk field elsewhere). */
export function useMilkSuggestion(methodId: number | null) {
  return useQuery({
    queryKey: keys.milkSuggestion(methodId),
    queryFn: () => api.get<MilkSuggestion>(`/suggestions/milk${qs({ methodId: methodId ?? undefined })}`),
  });
}

/** The method's learned drip and lag, which the guided brew's cut-off cue tells the barista when to stop the pump from. */
export function useCutoffSuggestion(methodId: number | null) {
  return useQuery({
    queryKey: keys.cutoffSuggestion(methodId),
    queryFn: () => api.get<CutoffSuggestion>(`/suggestions/cutoff${qs({ methodId: methodId ?? undefined })}`),
    enabled: methodId != null,
  });
}

// ---------------------------------------------------------------- stats / settings / info / backup
export function useStats() {
  return useQuery({ queryKey: keys.stats, queryFn: () => api.get<Stats>('/stats') });
}

export function useSettings() {
  return useQuery({ queryKey: keys.settings, queryFn: () => api.get<Settings>('/settings'), staleTime: 60_000 });
}
export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SettingsUpdate) => api.put<Settings>('/settings', patch),
    onSuccess: (s, patch) => {
      const before = qc.getQueryData<Settings>(keys.settings);
      if (before) {
        for (const key of Object.keys(patch) as Array<keyof Settings>) {
          if (key === 'timeZone' || sameSettingValue(before[key], s[key])) continue;
          track('setting_changed', { setting: key, toDefault: sameSettingValue(s[key], DEFAULT_SETTINGS[key]) });
        }
      }
      qc.setQueryData(keys.settings, s);
      // The server buckets days in this zone, so today's counts and the daily chart may change.
      if ('timeZone' in patch) void qc.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

export const serverInfoQuery = { queryKey: keys.info, queryFn: () => api.get<ServerInfo>('/info') };

export function useServerInfo() {
  return useQuery({ ...serverInfoQuery, staleTime: Infinity });
}

// ---------------------------------------------------------------- usage data (docs/usage.md)
/** Whether this account shares usage data, and its id. Only asked of a server that collects (ServerInfo.usage). */
export function useUsage() {
  const collecting = useServerInfo().data?.usage === true;
  return useQuery({ queryKey: keys.usage, queryFn: () => api.get<UsageStatus>('/usage'), enabled: collecting, staleTime: 60_000 });
}

/** The person's answer. Turning it off stops this device's events before the server has even heard. */
export function useSetUsageSharing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (share: boolean) => {
      if (!share) setUsageSharing(false);
      return api.put<UsageStatus>('/usage', { share });
    },
    onSuccess: (status) => {
      setUsageSharing(status.collecting && status.share);
      qc.setQueryData(keys.usage, status);
    },
  });
}

/** A new random id for this account's events. */
export function useNewUsageId() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<UsageStatus>('/usage/id'),
    onSuccess: (status) => qc.setQueryData(keys.usage, status),
  });
}

/** Hosted edition: deletes the account and everything in it (not under /api: the Worker handles it). */
export function useDeleteAccount() {
  return useMutation({
    mutationFn: async () => {
      const res = await fetch('/auth/account/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: 'delete' }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new ApiRequestError(body.error ?? `Deleting the account failed (${res.status})`, res.status);
      }
    },
  });
}

// ---------------------------------------------------------------- Ask Taretto's chats
// On a self-hosted server only: Taretto Cloud keeps them on each device (pages/ask/chatStore.ts).
export function useChats() {
  return useQuery({ queryKey: keys.chats, queryFn: () => api.get<ChatSummary[]>('/chats') });
}
export function useChat(id: number | null) {
  return useQuery({ queryKey: keys.chat(id ?? 0), queryFn: () => api.get<Chat>(`/chats/${id}`), enabled: id != null });
}
function onChatSaved(qc: QueryClient, chat: Chat) {
  qc.setQueryData(keys.chat(chat.id), chat);
  void qc.invalidateQueries({ queryKey: keys.chats });
}
/** A question's outcome, as the next in chat `id`, or as a new chat's first with null. */
export function useSaveChatExchange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, exchange }: { id: number | null; exchange: ChatAppend }) =>
      id == null ? api.post<Chat>('/chats', { exchanges: [exchange] }) : api.post<Chat>(`/chats/${id}/exchanges`, exchange),
    onSuccess: (chat) => onChatSaved(qc, chat),
  });
}
/** A chat with all its questions, as one moved over from a device. */
export function createChat(qc: QueryClient, exchanges: ChatExchangeCreate[]): Promise<Chat> {
  return api.post<Chat>('/chats', { exchanges }).then((chat) => {
    onChatSaved(qc, chat);
    return chat;
  });
}
export function useDeleteChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/chats/${id}`),
    onSettled: (_result, _error, id) => {
      qc.removeQueries({ queryKey: keys.chat(id) });
      void qc.invalidateQueries({ queryKey: keys.chats });
    },
  });
}
export function useDeleteChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/chats'),
    onSettled: () => {
      qc.removeQueries({ queryKey: ['chat'] });
      void qc.invalidateQueries({ queryKey: keys.chats });
    },
  });
}

/**
 * Full backup as a .zip attachment (Content-Disposition), for a plain `<a href download>` link.
 * Not fetch + Blob + createObjectURL: iPadOS 16 Safari reads a blob: download only after the user confirms its
 * "Download…?" prompt, so a URL revoked shortly after click() failed there (WebKitBlobResource error 1). A link to
 * the server has no such race, works on plain HTTP too, and the page never holds the whole backup in memory.
 */
export const BACKUP_DOWNLOAD_URL = '/api/backup?download=1';
/** The same without bag photos: a fraction of the size. */
export const BACKUP_DOWNLOAD_NO_PHOTOS_URL = '/api/backup?download=1&photos=0';

/** A backup archive goes as the file it is (a Blob of type application/zip), a JSON backup as compact JSON. */
export function useRestoreBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (backup: Backup | Blob) => api.post<{ ok: true; counts: Record<string, number> }>('/backup', backup),
    onSuccess: () => void qc.invalidateQueries(),
  });
}
